import { z } from 'zod';

export type ColorMode = 'light' | 'dark';

// Default colors. Warm, paper-toned palette so an unconfigured event reads as a
// considered surface out of the box. Neutrals lean warm (not the old cool slate)
// to sit under the Sunfish amber. primaryForeground is a warm near-black because
// white on amber fails contrast.
const DEFAULT_COLORS = {
	primary: '#f59e0b',
	primaryForeground: '#1f1604',
	secondary: '#ece7df',
	secondaryForeground: '#3a352c',
	background: '#fbf9f5',
	foreground: '#1c1a16',
	destructive: '#dc2626',
	destructiveForeground: '#ffffff',
	info: '#0ea5e9',
	infoForeground: '#ffffff',
	layout: '#211c16',
	layoutForeground: '#f7f2e9',
	// Optional roles. Previously unset, which left their utilities unstyled on the
	// branded surface; populating them lights up muted text, accents, and borders.
	muted: '#f1ece3',
	mutedForeground: '#6e665a',
	accent: '#f1ece3',
	accentForeground: '#1c1a16',
	border: '#ddd4c2',
	input: '#ddd4c2',
	ring: '#f59e0b',
	// card/cardForeground are intentionally absent: branded surfaces inherit the
	// app's --card theme token (matching the last release) rather than a generic
	// slate override that clashed with the registration stepper in dark mode.
	// Forced-white foreground for chip/tag/pill labels riding on the brand
	// color. primaryForeground is the readable-contrast derivative used for
	// body buttons; the contrast algorithm picks dark text on a mid-light
	// brand, which makes pills like "LIMITED QUANTITY" look mis-set. Tags
	// want a single bold ink regardless of brand luminance — overridable per
	// tenant if their brand is so light that white fails.
	tagBrandForeground: '#ffffff'
};

export const brandingSchema = z.object({
	primary: z
		.string({ message: 'Primary color is required' })
		.min(1, 'Primary color is required')
		.default(DEFAULT_COLORS.primary),
	primaryForeground: z
		.string({ message: 'Primary color text is required' })
		.min(1, 'Primary color text is required')
		.default(DEFAULT_COLORS.primaryForeground),
	secondary: z
		.string({ message: 'Secondary color is required' })
		.min(1, 'Secondary color is required')
		.default(DEFAULT_COLORS.secondary),
	secondaryForeground: z
		.string({ message: 'Secondary color text is required' })
		.min(1, 'Secondary color text is required')
		.default(DEFAULT_COLORS.secondaryForeground),
	info: z.string({ message: 'Info color is required' }).min(1, 'Info color is required').default(DEFAULT_COLORS.info),
	infoForeground: z
		.string({ message: 'Info color text is required' })
		.min(1, 'Info color text is required')
		.default(DEFAULT_COLORS.infoForeground),
	destructive: z
		.string({ message: 'Error color is required' })
		.min(1, 'Error color is required')
		.default(DEFAULT_COLORS.destructive),
	destructiveForeground: z
		.string({ message: 'Error color text is required' })
		.min(1, 'Error color text is required')
		.default(DEFAULT_COLORS.destructiveForeground),
	background: z
		.string({ message: 'Background color is required' })
		.min(1, 'Background color is required')
		.default(DEFAULT_COLORS.background),
	foreground: z
		.string({ message: 'Text color is required' })
		.min(1, 'Foreground color is required')
		.default(DEFAULT_COLORS.foreground),
	layout: z
		.string({ message: 'Layout color is required' })
		.min(1, 'Layout color is required')
		.default(DEFAULT_COLORS.layout),
	layoutForeground: z
		.string({ message: 'Layout color text is required' })
		.min(1, 'Layout color text is required')
		.default(DEFAULT_COLORS.layoutForeground)
});

export const brandingFormSchema = z.object({
	currentColorMode: z.enum(['light', 'dark']).default('light'),
	light: brandingSchema,
	dark: brandingSchema,
	logoAssignmentId: z.string().optional().nullable()
});

export { DEFAULT_COLORS };
